import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-hot-toast';
import {
    FaTrash,
    FaPlus,
    FaChevronDown,
    FaChevronRight,
    FaCheck,
    FaTimes,
    FaSpinner
} from 'react-icons/fa/index.js';
import callOysterApi from '../../utils/call-oyster-api.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import '../../styles/ManageTeamsModal.css';

import useCurrentUser from '../../hooks/useCurrentUser.js';

const AVAILABLE_SHIFTS = ['Sundancers', 'Moonwalkers', 'Midknight Crawlers'];

const ManageTeamsModal = ({ isOpen, onClose, onTeamsUpdated }) => {
    const { user: currentUserData } = useCurrentUser();
    const [teams, setTeams] = useState([]);
    const [newTeamName, setNewTeamName] = useState('');
    const [loading, setLoading] = useState(false);
    const [adding, setAdding] = useState(false);
    const [expandedTeamId, setExpandedTeamId] = useState(null);
    const [editingTeam, setEditingTeam] = useState(null);
    const [savingTeamId, setSavingTeamId] = useState(null);

    // Helpers for permissions
    const isLeadership = currentUserData?.isItLeadership;
    const isTeamLead = currentUserData?.isTeamLead;
    const myTeamName = currentUserData?.hdTeam;

    const canCreateTeam = isLeadership;
    const canDeleteTeam = isLeadership;

    const canEditTeam = (team) => {
        if (isLeadership) return true;
        if (isTeamLead) {
            // Can edit own team
            return team.name === myTeamName;
        }
        return false;
    };

    const fetchTeams = async () => {
        try {
            setLoading(true);
            const response = await callOysterApi('/teams', 'GET');
            if (response.ok) {
                const data = await response.json();
                if (data.success && Array.isArray(data.payload)) {
                    setTeams(data.payload);
                }
            }
        } catch (error) {
            console.error('Error fetching teams:', error);
            toast.error('Failed to load teams');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            fetchTeams();
            setExpandedTeamId(null);
            setEditingTeam(null);
        }
    }, [isOpen]);

    const handleAddTeam = async (e) => {
        e.preventDefault();
        if (!canCreateTeam) return;
        if (!newTeamName.trim()) return;

        try {
            setAdding(true);
            const response = await callOysterApi('/teams', 'POST', {
                name: newTeamName
            });
            if (response.ok) {
                toast.success('Team created successfully');
                setNewTeamName('');
                fetchTeams();
                if (onTeamsUpdated) onTeamsUpdated();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to create team');
            }
        } catch (error) {
            console.error('Error creating team:', error);
            toast.error('Error creating team');
        } finally {
            setAdding(false);
        }
    };

    const handleDeleteTeam = async (teamName) => {
        if (!canDeleteTeam) return;

        const team = teams.find((t) => t.name === teamName);
        const userCount = team?.userCount || 0;

        let message = `Are you sure you want to delete team "${teamName}"? This action cannot be undone.`;
        if (userCount > 0) {
            message += `\n\nWARNING: This team has ${userCount} user(s) assigned. Deleting it will unassign them.`;
        }

        // eslint-disable-next-line no-alert
        if (!window.confirm(message)) {
            return;
        }

        try {
            const response = await callOysterApi(
                `/teams/${encodeURIComponent(teamName)}`,
                'DELETE'
            );
            if (response.ok) {
                toast.success('Team deleted successfully');
                setExpandedTeamId(null);
                setEditingTeam(null);
                fetchTeams();
                if (onTeamsUpdated) onTeamsUpdated();
            } else {
                toast.error('Failed to delete team');
            }
        } catch (error) {
            console.error('Error deleting team:', error);
            toast.error('Error deleting team');
        }
    };

    const handleExpandTeam = (team) => {
        if (expandedTeamId === team._id) {
            setExpandedTeamId(null);
            setEditingTeam(null);
        } else {
            setExpandedTeamId(team._id);
            // Even if read-only, we can expand to view details
            setEditingTeam({
                ...team,
                shifts: [...(team.shifts || AVAILABLE_SHIFTS)]
            });
        }
    };

    const handleNameChange = (newName) => {
        setEditingTeam((prev) => ({ ...prev, name: newName }));
    };

    const handleShiftToggle = (shift) => {
        setEditingTeam((prev) => {
            const currentShifts = prev.shifts || [];
            if (currentShifts.includes(shift)) {
                // Remove shift
                return {
                    ...prev,
                    shifts: currentShifts.filter((s) => s !== shift)
                };
            }

            return {
                ...prev,
                shifts: [...currentShifts, shift]
            };
        });
    };

    const handleSaveTeam = async () => {
        if (!editingTeam) return;
        // RBAC check again just in case
        const originalTeamForCheck = teams.find(
            (t) => t._id === editingTeam._id
        );
        if (!canEditTeam(originalTeamForCheck)) {
            toast.error("You don't have permission to edit this team.");
            return;
        }

        // Check for removed shifts and affected users
        const originalTeam = teams.find((t) => t._id === editingTeam._id);
        if (originalTeam) {
            const originalShifts = originalTeam.shifts || [];
            const newShifts = editingTeam.shifts || [];
            const removedShifts = originalShifts.filter(
                (s) => !newShifts.includes(s)
            );

            let affectedCount = 0;
            const warningDetails = [];

            removedShifts.forEach((shift) => {
                const count = originalTeam.shiftCounts?.[shift] || 0;
                if (count > 0) {
                    affectedCount += count;
                    warningDetails.push(`- ${shift}: ${count} user(s)`);
                }
            });

            if (affectedCount > 0) {
                const message = `Warning: You are removing shifts that have users assigned:\n\n${warningDetails.join(
                    '\n'
                )}\n\nThis will unassign ${affectedCount} user(s) from their shifts.\n\nAre you sure you want to proceed?`;
                // eslint-disable-next-line no-alert
                if (!window.confirm(message)) return;
            }
        }

        try {
            setSavingTeamId(editingTeam._id);
            const response = await callOysterApi(
                `/teams/${editingTeam._id}`,
                'PUT',
                {
                    name: editingTeam.name,
                    shifts: editingTeam.shifts
                }
            );
            if (response.ok) {
                toast.success('Team updated successfully');
                fetchTeams();
                if (onTeamsUpdated) onTeamsUpdated();
            } else {
                const data = await response.json();
                toast.error(data.message || 'Failed to update team');
            }
        } catch (error) {
            console.error('Error updating team:', error);
            toast.error('Error updating team');
        } finally {
            setSavingTeamId(null);
        }
    };

    const handleCancelEdit = () => {
        const originalTeam = teams.find((t) => t._id === expandedTeamId);
        if (originalTeam) {
            setEditingTeam({
                ...originalTeam,
                shifts: [...(originalTeam.shifts || AVAILABLE_SHIFTS)]
            });
        }
        // Could also just collapse? standard UX is revert changes.
    };

    const hasChanges = () => {
        if (!editingTeam || !expandedTeamId) return false;
        const originalTeam = teams.find((t) => t._id === expandedTeamId);
        if (!originalTeam) return false;

        const nameChanged = editingTeam.name !== originalTeam.name;
        const originalShifts = [...(originalTeam.shifts || [])].sort();
        const editingShifts = [...(editingTeam.shifts || [])].sort();
        const shiftsChanged =
            JSON.stringify(originalShifts) !== JSON.stringify(editingShifts);

        return nameChanged || shiftsChanged;
    };

    if (!isOpen) return null;

    return (
        <div className="manage-teams-modal-overlay">
            <div className="modal-content manage-teams-modal">
                <div className="modal-header">
                    <h2>Manage Teams</h2>
                    <button className="close-button" onClick={onClose}>
                        &times;
                    </button>
                </div>

                <div className="modal-body">
                    {/* Add Team Form - Only for Leadership */}
                    {canCreateTeam && (
                        <form
                            onSubmit={handleAddTeam}
                            className="add-team-form">
                            <input
                                type="text"
                                placeholder="Enter new team name"
                                value={newTeamName}
                                onChange={(e) => setNewTeamName(e.target.value)}
                                className="form-input"
                                disabled={adding}
                            />
                            <button
                                type="submit"
                                className="btn-add"
                                disabled={adding || !newTeamName.trim()}>
                                {adding ? (
                                    <>
                                        <FaSpinner className="spinner" />{' '}
                                        {'Adding...'}
                                    </>
                                ) : (
                                    <>
                                        <FaPlus /> Add Team
                                    </>
                                )}
                            </button>
                        </form>
                    )}

                    {loading ? (
                        <div className="loading-container">
                            <LoadingSpinner />
                        </div>
                    ) : (
                        <div className="teams-list-container">
                            {teams.length === 0 ? (
                                <p className="no-data">No teams found.</p>
                            ) : (
                                <ul className="teams-list">
                                    {teams.map((team) => {
                                        const userCanEditThisTeam =
                                            canEditTeam(team);
                                        return (
                                            <li
                                                key={team._id || team.name}
                                                className={`team-item ${
                                                    expandedTeamId === team._id
                                                        ? 'expanded'
                                                        : ''
                                                }`}>
                                                <div
                                                    className="team-row"
                                                    onClick={() =>
                                                        handleExpandTeam(team)
                                                    }>
                                                    <span className="expand-icon">
                                                        {expandedTeamId ===
                                                        team._id ? (
                                                            <FaChevronDown />
                                                        ) : (
                                                            <FaChevronRight />
                                                        )}
                                                    </span>
                                                    <div className="team-info">
                                                        <span className="team-name">
                                                            {team.name}
                                                        </span>
                                                        <span className="team-shifts">
                                                            {team.shifts?.join(
                                                                ', '
                                                            ) || 'No shifts'}
                                                        </span>
                                                    </div>
                                                    {canDeleteTeam && (
                                                        <button
                                                            className="btn-delete"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteTeam(
                                                                    team.name
                                                                );
                                                            }}
                                                            title="Delete Team">
                                                            <FaTrash />
                                                        </button>
                                                    )}
                                                </div>

                                                {expandedTeamId === team._id &&
                                                    editingTeam && (
                                                        <div className="team-edit-panel">
                                                            <div className="edit-row">
                                                                <label>
                                                                    Name:
                                                                </label>
                                                                <input
                                                                    type="text"
                                                                    value={
                                                                        editingTeam.name
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleNameChange(
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    className="edit-input"
                                                                    disabled={
                                                                        !userCanEditThisTeam
                                                                    }
                                                                />
                                                            </div>

                                                            <div className="edit-row">
                                                                <label>
                                                                    Shifts:
                                                                </label>
                                                                <div className="shifts-checkboxes">
                                                                    {AVAILABLE_SHIFTS.map(
                                                                        (
                                                                            shift
                                                                        ) => (
                                                                            <label
                                                                                key={
                                                                                    shift
                                                                                }
                                                                                className="shift-checkbox">
                                                                                <input
                                                                                    type="checkbox"
                                                                                    checked={editingTeam.shifts?.includes(
                                                                                        shift
                                                                                    )}
                                                                                    onChange={() =>
                                                                                        handleShiftToggle(
                                                                                            shift
                                                                                        )
                                                                                    }
                                                                                    disabled={
                                                                                        !userCanEditThisTeam
                                                                                    }
                                                                                />
                                                                                {
                                                                                    shift
                                                                                }
                                                                            </label>
                                                                        )
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {userCanEditThisTeam && (
                                                                <div className="edit-actions">
                                                                    <button
                                                                        className="btn-save"
                                                                        onClick={
                                                                            handleSaveTeam
                                                                        }
                                                                        disabled={
                                                                            !hasChanges() ||
                                                                            savingTeamId ===
                                                                                editingTeam._id
                                                                        }>
                                                                        {savingTeamId ===
                                                                        editingTeam._id ? (
                                                                            <>
                                                                                <FaSpinner className="spinner" />{' '}
                                                                                Saving...
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <FaCheck />{' '}
                                                                                Save
                                                                            </>
                                                                        )}
                                                                    </button>
                                                                    <button
                                                                        className="btn-cancel"
                                                                        onClick={
                                                                            handleCancelEdit
                                                                        }
                                                                        disabled={
                                                                            !hasChanges()
                                                                        }>
                                                                        <FaTimes />{' '}
                                                                        Cancel
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

ManageTeamsModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onTeamsUpdated: PropTypes.func
};

export default ManageTeamsModal;
